/**
 * The Interval macro — a repeating flow pulse built from standard nodes.
 *
 * Canonical expansion — fire-then-re-arm, re-entered through a custom event
 * (#896). Listed in emission order, which is already a valid KHR order: values
 * come from earlier nodes and every flow targets a LATER node, so the ratified
 * spec's "no directed cycles" rule holds without reordering.
 *
 *     [receive: event/receive]  event = E    ← entry, activated by `send`
 *         out → gate.in
 *     [running: variable/get]   variable = V (bool, private)
 *     [gate: flow/branch]       condition ← running.value
 *         true → tick.in                     (a period delivered after a cancel is dropped)
 *     [tick: flow/sequence]                  ← once per elapsed period
 *         '0' → (payload chain — the macro's `done`, optional)
 *         '1' → delay.cancel                 (drop anything pending…)
 *         '2' → delay.in                     (…then re-arm: never two pending)
 *     [restart: flow/sequence]               ← macro entry `in`
 *         '0' → delay.cancel                 (drop any pending cycle)
 *         '1' → delay.in                     (arm)
 *         '2' → (immediate continue — the macro's `out`, optional)
 *         '3' → arm.in
 *     [arm: variable/set V = true]
 *     [clear: variable/set V = false]        ← macro entry `cancel`
 *     [delay: flow/setDelay]  duration = interval seconds
 *         done → send.in
 *     [send: event/send]  event = E
 *
 * E is an INTERNAL custom event (no `id`, no values) and V a bool variable,
 * both appended per expansion, so two Intervals never cross-talk. The loop
 * closes through the event, not a flow edge: `event/receive` is an entry node,
 * and the spec names custom events as the way to build "asynchronous loops"
 * (Concepts § Custom Events). Graph teardown stops the loop through the delay
 * block's lifecycle. The flag writes are leaves fed by sequences, never
 * chained through `variable/set.out`: the ratified spec has that socket, but
 * Babylon 9.27.1's strict loader drops it as pre-ratification compatibility.
 *
 * Event delivery — the spec leaves custom-event semantics to the application.
 * Babylon's KHR_interactivity loader defers every custom event to the next
 * frame (`dispatchEventsSynchronously = false`), and the native compiler
 * delivers internal events the same way (TraceInternalEventBlocks), so both
 * runtimes run this identically. Consequences, all deliberate:
 * - Each period's payload fires on the frame AFTER the delay elapses.
 * - `cancel` only clears the flag. A pending delay still elapses and sends,
 *   and the gate drops that delivery — so cancel is exact on every client,
 *   including a cancel landing between a send and its delivery. (The flag is
 *   load-bearing: without it a late delivery would re-arm the loop.) A
 *   restart cancels any pending delay itself, so the stale one never doubles
 *   the loop.
 * - A restart landing in that same one-frame window lets the in-flight
 *   delivery through (the flag is set again): that period's payload fires
 *   and the loop re-arms from the delivery. Still one loop — the tick
 *   cancels before it re-arms.
 *
 * Semantics (honest — these are the composition's, on every client):
 * - `in` (re)starts: any pending cycle is cancelled first, then the delay
 *   arms and the flag is set. The continue socket fires immediately.
 * - Each period: delay elapses → send → (next frame) receive → payload chain
 *   → re-arm. The period is fire-then-re-arm, so it includes one frame of
 *   event delivery, the payload's synchronous cost and frame quantization
 *   (`flow/setDelay` checks its timer on render ticks). Periods do not
 *   resync to absolute deadlines.
 * - `flow/setDelay` reads `duration` each time it arms, so a wired interval
 *   value is re-read every cycle; re-triggering `in` applies it immediately.
 * - A payload may re-trigger `in` (a forward wire now, not a cycle); the
 *   loop stays single.
 *
 * Authoring guard: a 0-second period is meaningless (it degrades to "once per
 * frame" — the deferred event is the only thing pacing the loop), and
 * `flow/setDelay` errs negative/NaN into a dead loop. An invalid inline period
 * expands WITHOUT the arm wire — stored data stays standard-ops-only and the
 * loop simply never starts. Wired durations can't be checked at authoring
 * time; setDelay rejects negative/NaN at runtime.
 *
 * The pre-#896 cyclic expansion (restart / delay / tick, re-armed by
 * `tick.'1' → delay.in`) is `legacyShapes[0]`: stored groups in that shape are
 * raised as Interval and re-lowered in this shape at ingestion.
 */

import { GraphOps } from '../../../scenedata/behaviors/behaviorgraphdata';
import type { FlowTarget, GraphData } from '../../../scenedata/behaviors/behaviorgraphdata';
import { expandMacro, registerMacro } from './registry';
import type { MacroDefinition, MacroGraphWriter, MacroParamDef, MacroShape } from './registry';
import { recognizeMacros } from './recognize';

/** Macro name for the Interval canned composition. */
export const INTERVAL_MACRO = 'interval';

/** Catalog pseudo-op naming the Interval macro as a single high-level node. */
export const INTERVAL_MACRO_OP = 'macro/interval' as const;

/** Node roles within an Interval expansion. */
export type IntervalMacroRole = 'receive' | 'running' | 'gate' | 'tick' | 'restart' | 'arm' | 'clear' | 'delay' | 'send';

/** Restart-sequence socket that continues immediately after arming (the macro's `out`). */
export const INTERVAL_CONTINUE_SOCKET = '2';
/** Tick-sequence socket the payload (done) chain attaches to. */
export const INTERVAL_DONE_SOCKET = '0';
/** The macro's cancel input (lands on the `clear` flag write's `in`). */
export const INTERVAL_CANCEL_SOCKET = 'cancel';

/** The macro's value input naming its period. */
export const INTERVAL_PERIOD_PARAM = 'interval';

const INTERVAL_PARAMS: Readonly<Record<string, MacroParamDef>> = {
    [INTERVAL_PERIOD_PARAM]: {
        targets: [{ role: 'delay', socket: 'duration' }],
        validateLiteral: value =>
            typeof value === 'number' && value > 0 && Number.isFinite(value)
                ? null
                : `must be a positive, finite number of seconds (got ${JSON.stringify(value)})`,
    },
};

/**
 * The pre-#896 expansion: a fire-then-re-arm FLOW CYCLE (`tick.'1' →
 * delay.in`), which the ratified KHR spec forbids. Recognized so stored data
 * upgrades at ingestion; never emitted.
 */
const LEGACY_CYCLIC_SHAPE: MacroShape = {
    nodes: [
        { role: 'restart', op: GraphOps.FLOW_SEQUENCE },
        { role: 'delay', op: GraphOps.FLOW_SET_DELAY },
        { role: 'tick', op: GraphOps.FLOW_SEQUENCE },
    ],
    wires: [
        { from: 'restart', socket: '0', to: 'delay', toSocket: 'cancel' },
        { from: 'restart', socket: '1', to: 'delay', toSocket: 'in' },
        { from: 'delay', socket: 'done', to: 'tick', toSocket: 'in' },
        { from: 'tick', socket: '1', to: 'delay', toSocket: 'in' },
    ],
    entryRole: 'restart',
    inputs: {
        in: { role: 'restart', socket: 'in' },
        [INTERVAL_CANCEL_SOCKET]: { role: 'delay', socket: 'cancel' },
    },
    defaultInput: 'in',
    outputs: {
        out: { role: 'restart', socket: INTERVAL_CONTINUE_SOCKET },
        done: { role: 'tick', socket: INTERVAL_DONE_SOCKET },
    },
    params: INTERVAL_PARAMS,
    disarm: [{ from: 'restart', socket: '1' }],
};

export const INTERVAL_MACRO_DEF: MacroDefinition = {
    name: INTERVAL_MACRO,
    op: INTERVAL_MACRO_OP,
    // Emission order is a valid KHR order: values come from earlier nodes,
    // flows go to later ones.
    nodes: [
        { role: 'receive', op: GraphOps.EVENT_RECEIVE },
        { role: 'running', op: GraphOps.VARIABLE_GET },
        { role: 'gate', op: GraphOps.FLOW_BRANCH },
        { role: 'tick', op: GraphOps.FLOW_SEQUENCE },
        { role: 'restart', op: GraphOps.FLOW_SEQUENCE },
        { role: 'arm', op: GraphOps.VARIABLE_SET, constants: { value: true } },
        { role: 'clear', op: GraphOps.VARIABLE_SET, constants: { value: false } },
        { role: 'delay', op: GraphOps.FLOW_SET_DELAY },
        { role: 'send', op: GraphOps.EVENT_SEND },
    ],
    wires: [
        // Each elapsed period re-enters here — only while running (a cancel
        // may land between the send and its deferred delivery) — then the
        // payload chain, then re-arm, cancelling first so at most one
        // activation is ever pending.
        { from: 'receive', socket: 'out', to: 'gate', toSocket: 'in' },
        { from: 'gate', socket: 'true', to: 'tick', toSocket: 'in' },
        { from: 'tick', socket: '1', to: 'delay', toSocket: 'cancel' },
        { from: 'tick', socket: '2', to: 'delay', toSocket: 'in' },
        // Entry: cancel any pending cycle, arm, mark running. The flag writes
        // are terminal nodes fed by a sequence rather than chained through
        // `variable/set.out`: Babylon 9.27.1's strict loader drops that
        // (ratified) socket as "compatibility only", so a chain through it
        // would silently end there in a strict viewer.
        { from: 'restart', socket: '0', to: 'delay', toSocket: 'cancel' },
        { from: 'restart', socket: '1', to: 'delay', toSocket: 'in' },
        { from: 'restart', socket: '3', to: 'arm', toSocket: 'in' },
        // Cancel is the `clear` write alone (a macro input, no wire here): a
        // delay still pending elapses and its delivery is dropped at the gate.
        // The period elapses → the private event loops back to `receive`.
        { from: 'delay', socket: 'done', to: 'send', toSocket: 'in' },
    ],
    valueWires: [{ from: 'running', socket: 'value', to: 'gate', toSocket: 'condition' }],
    events: [{ roles: ['send', 'receive'] }],
    variables: [{ name: 'running', type: 'bool', initial: false, roles: ['running', 'arm', 'clear'] }],
    entryRole: 'restart',
    inputs: {
        in: { role: 'restart', socket: 'in' },
        [INTERVAL_CANCEL_SOCKET]: { role: 'clear', socket: 'in' },
    },
    defaultInput: 'in',
    outputs: {
        out: { role: 'restart', socket: INTERVAL_CONTINUE_SOCKET },
        done: { role: 'tick', socket: INTERVAL_DONE_SOCKET },
    },
    params: INTERVAL_PARAMS,
    // Dropping the arm wire leaves the composition inert.
    disarm: [{ from: 'restart', socket: '1' }],
    legacyShapes: [LEGACY_CYCLIC_SHAPE],
};

registerMacro(INTERVAL_MACRO_DEF);

// ============================================================================
// Interval-shaped conveniences
// ============================================================================

/** Node indices of one Interval expansion, plus its shared instance id. */
export interface IntervalMacroNodes {
    macroId: string;
    /** `event/receive` entry the loop re-enters through (private internal event). */
    receive: number;
    /** `flow/sequence` entry — wire external flow into its `in` to (re)start; socket `'2'` continues immediately. */
    restart: number;
    /** `variable/set running = false` behind the macro's `cancel` — wire external flow into its `in` to stop the loop. */
    clear: number;
    /** `flow/setDelay` timing each period. */
    delay: number;
    /** `flow/sequence` fired once per period — socket `'0'` is the payload (done) chain. */
    tick: number;
    /** `event/send` fired when the delay elapses. */
    send: number;
    /** Index of the expansion's private event in `GraphData.events`. */
    event: number;
    /** Index of the expansion's private `running` flag in `GraphData.variables`. */
    variable: number;
}

/** A recognized Interval expansion within a GraphData. */
export interface IntervalMacroInstance {
    macroId: string;
    /** Node index of the entry `flow/sequence`. */
    restart: number;
    /** Node index of the `flow/setDelay`. */
    delay: number;
    /** Node index of the per-period `flow/sequence`. */
    tick: number;
    /** Node index of the `event/receive` entry. */
    receive: number;
    /** Node index of the `event/send`. */
    send: number;
    /** Node index of the `variable/set` the macro's `cancel` lands on. */
    clear: number;
    /** Index of the private event in `GraphData.events`. */
    event: number;
    /** Index of the private `running` flag in `GraphData.variables`. */
    variable: number;
    /** Authored inline period in seconds; undefined when `duration` is wired from another node. */
    seconds?: number;
    /** Target of the payload (done) chain, when wired. */
    doneTarget?: FlowTarget;
    /** Target of the immediate-continue (out) chain, when wired. */
    outTarget?: FlowTarget;
}

/**
 * Emit the canonical Interval expansion into a graph writer.
 *
 * @param writer Graph writer (typically a `GraphBuilder`).
 * @param seconds Interval period in seconds (inline literal on the delay's `duration`).
 * @param macroId Shared instance id; generated when omitted.
 * @returns Node indices of the expansion. The caller wires external flow into
 *   `restart` (socket `in`) to start, and attaches the payload chain to
 *   `tick` socket `'0'`.
 */
export function expandIntervalMacro(writer: MacroGraphWriter, seconds: number, macroId?: string): IntervalMacroNodes {
    const { macroId: id, nodes, events, variables } = expandMacro(
        writer,
        INTERVAL_MACRO_DEF,
        { [INTERVAL_PERIOD_PARAM]: seconds },
        macroId,
    );
    return {
        macroId: id,
        receive: nodes['receive'],
        tick: nodes['tick'],
        restart: nodes['restart'],
        delay: nodes['delay'],
        send: nodes['send'],
        clear: nodes['clear'],
        event: events[0],
        variable: variables[0],
    };
}

/**
 * Find current-shape Interval expansions — `recognizeMacros` narrowed to
 * Interval and re-shaped by role name for callers that know the composition
 * (tests, diagnostics). Legacy-shape groups are left out: ingestion re-lowers
 * them, and they have none of the current roles.
 */
export function recognizeIntervalMacros(graph: GraphData): IntervalMacroInstance[] {
    return recognizeMacros(graph, INTERVAL_MACRO).filter(instance => !instance.legacy).map(instance => {
        const seconds = instance.params[INTERVAL_PERIOD_PARAM];
        return {
            macroId: instance.macroId,
            restart: instance.nodes['restart'],
            delay: instance.nodes['delay'],
            tick: instance.nodes['tick'],
            receive: instance.nodes['receive'],
            send: instance.nodes['send'],
            clear: instance.nodes['clear'],
            event: instance.events[0],
            variable: instance.variables[0],
            ...(typeof seconds === 'number' ? { seconds } : {}),
            ...(instance.outputs['done'] ? { doneTarget: instance.outputs['done'] } : {}),
            ...(instance.outputs['out'] ? { outTarget: instance.outputs['out'] } : {}),
        };
    });
}
